import type { Home, ResidentBody } from '@lmc/ts-common'
import { Button, EmptyState, Form, PageHeader } from '@lmc/ui-kit'
import type { AccessInformation, Environment } from '@lmc/validations'
import { Form as AntdForm } from 'antd'
import { getNominative, showToast } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import dayjs from 'dayjs'
import useResident, {
  convertFormTypeToResidentBody,
  type ResidentUpdateBody
} from 'hooks/useResident'
import { NAMESPACE } from 'locale'
import { serviceUserProfileConstants as supConstants } from 'locale/serviceUserProfile'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ButtonsWrapper,
  ProfileContent,
  ProfileWrapper
} from '../../styledComponents'
import { handleOnFinishFailed, scrollToTop } from '../../utils/utils'
import { AccessInformationFormCard } from './FormCards/AccessInformation'
import { CarersFormCard } from './FormCards/Carers'
import { EnvironmentFormCard } from './FormCards/Environment'
import { LocationFormCard } from './FormCards/Location'
import { ZoneFormCard } from './FormCards/Zone'

type FormType = {
  environment?: Omit<
    Environment,
    'dateOfObservation' | 'updatedAt' | 'updatedBy' | 'updatedByData'
  > & {
    dateOfObservation?: dayjs.Dayjs | null
  }
  accessInformation?: Omit<AccessInformation, 'dateOfObservation'> & {
    dateOfObservation?: dayjs.Dayjs | null
  }
}

const ResidentSiteInformation = ({ testId }: { testId?: string } = {}) => {
  const { t } = useTranslation(NAMESPACE.SERVICE_USER_PROFILE)
  const [form] = AntdForm.useForm()
  const navigate = useNavigate()
  const topRef = useRef<HTMLDivElement>(null)
  const { id } = useParams()
  const preferredPeopleNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredPeopleNominative
  )
  const residentNominative = getNominative(
    preferredPeopleNominative,
    'Resident'
  )
  const { resident, isLoading, updateResident } = useResident({
    id,
    onUpdateSuccess: () => {
      showToast({
        title: t(supConstants.RESIDENT_UPDATED, {
          variable: residentNominative
        })
      })
      navigate(`../profile`, { relative: 'path' })
    }
  })
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)

  const convertFormTypeToPartialResidentBody = (
    values: FormType
  ): ResidentUpdateBody => ({
    ...values,
    environment: {
      householdComposition: values.environment?.householdComposition,
      householdEnvironment: values.environment?.householdEnvironment,
      environmentDescription: values.environment?.environmentDescription,
      dateOfObservation:
        values.environment?.dateOfObservation?.toISOString() ?? null
    },
    accessInformation: {
      specialAccessRequirements:
        values.accessInformation?.specialAccessRequirements,
      dateOfObservation:
        values.accessInformation?.dateOfObservation?.toISOString() ?? null
    }
  })

  const handleOnFinish = async (values: FormType) => {
    try {
      setIsUpdateLoading(true)
      const convertedValues = convertFormTypeToResidentBody(values, (vals) =>
        convertFormTypeToPartialResidentBody(vals)
      )
      setIsDirty(false)
      await updateResident(convertedValues)
      setIsUpdateLoading(false)
    } catch {
      setIsDirty(true)
      setIsUpdateLoading(false)
    }
  }

  const handleOnCancel = () => {
    navigate(`../profile`, { relative: 'path' })
  }

  const handleFormValuesChange = (values: ResidentUpdateBody) => {
    if (values) {
      setIsDirty(true)
    }
  }

  useEffect(() => {
    scrollToTop(topRef)
  }, [])

  const hasRolePermission = useAbilityAccess([
    {
      i: 'update',
      this: 'Resident',
      field: 'status'
    }
  ])

  if (isLoading) {
    return (
      <Spinner
        data-testid={
          testId ? `${testId}-site-info-loading` : 'site-info-loading'
        }
      />
    )
  }

  if (resident === undefined) {
    return (
      <EmptyState
        title={t(supConstants.NO_RESIDENT)}
        data-testid={
          testId ? `${testId}-site-info-no-resident` : 'site-info-no-resident'
        }
      />
    )
  }

  const initialValues: Partial<FormType> = {
    ...resident,
    environment: {
      householdComposition: resident.environment?.householdComposition,
      householdEnvironment: resident.environment?.householdEnvironment,
      environmentDescription: resident.environment?.environmentDescription,
      dateOfObservation: resident.environment?.dateOfObservation
        ? dayjs(resident.environment.dateOfObservation)
        : null
    },
    accessInformation: {
      specialAccessRequirements:
        resident.accessInformation?.specialAccessRequirements,
      dateOfObservation: resident.accessInformation?.dateOfObservation
        ? dayjs(resident.accessInformation.dateOfObservation)
        : null
    }
  }

  return (
    <ProfileWrapper
      ref={topRef}
      data-testid={testId ? `${testId}-site-info-wrapper` : 'site-info-wrapper'}
    >
      <PageHeader
        sticky
        title={t(supConstants.SITE_RELATED_INFORMATION)}
        data-testid={testId ? `${testId}-site-info-header` : 'site-info-header'}
      >
        <ButtonsWrapper
          data-testid={
            testId ? `${testId}-site-info-buttons` : 'site-info-buttons'
          }
        >
          <Button
            data-cy="cancel-btn"
            onClick={handleOnCancel}
            data-testid={
              testId ? `${testId}-site-info-cancel-btn` : 'site-info-cancel-btn'
            }
          >
            {hasRolePermission ? t(supConstants.CANCEL) : t(supConstants.BACK)}
          </Button>
          <Button
            data-cy="submit-btn"
            variant="primary"
            onClick={form.submit}
            type="submit"
            isLoading={isUpdateLoading}
            disabledByPermission={!hasRolePermission}
            data-testid={
              testId ? `${testId}-site-info-save-btn` : 'site-info-save-btn'
            }
          >
            {t(supConstants.SAVE)}
          </Button>
        </ButtonsWrapper>
      </PageHeader>
      <ProfileContent
        data-testid={
          testId ? `${testId}-site-info-content` : 'site-info-content'
        }
      >
        <NavigationModal canNavigate={!isDirty} />
        <Form<FormType>
          form={form}
          disabled={!hasRolePermission}
          onFinish={handleOnFinish}
          initialValues={initialValues}
          onValuesChange={handleFormValuesChange}
          onFinishFailed={({ errorFields }) =>
            handleOnFinishFailed(errorFields)
          }
          data-testid={testId ? `${testId}-site-info-form` : 'site-info-form'}
        >
          <ZoneFormCard resident={resident} testId={testId} />
          <LocationFormCard testId={testId} />
          <CarersFormCard resident={resident} form={form} testId={testId} />
          <EnvironmentFormCard resident={resident} testId={testId} />
          <AccessInformationFormCard resident={resident} testId={testId} />
        </Form>
      </ProfileContent>
    </ProfileWrapper>
  )
}

export default ResidentSiteInformation
